import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SortColumn, SortDir } from "@/lib/filters";

export type Density = "compact" | "default" | "comfy";

export interface DataGridColumn<T> {
  id: string;
  header: string;
  className?: string;
  headerClassName?: string;
  cell: (row: T) => ReactNode;
  width?: string | number;
  /** When set, header is clickable and cycles sort for this column. */
  sortable?: SortColumn;
}

export function DataGrid<T extends { id: string }>({
  rows,
  columns,
  empty,
  pageSize = 80,
  className,
  density: densityProp,
  toolbar,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: T[];
  columns: DataGridColumn<T>[];
  empty?: ReactNode;
  pageSize?: number;
  className?: string;
  density?: Density;
  toolbar?: ReactNode;
  sortKey?: SortColumn | null;
  sortDir?: SortDir | null;
  onSort?: (column: SortColumn) => void;
}) {
  const [page, setPage] = useState(0);
  const [density, setDensity] = useState<Density>(densityProp ?? "default");

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const slice = useMemo(
    () => rows.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [rows, safePage, pageSize],
  );

  if (rows.length === 0) {
    return (
      empty ?? (
        <div className="empty panel">
          <div className="empty-icon">⌀</div>
          <strong>No rows</strong>
        </div>
      )
    );
  }

  return (
    <div
      className={cn(
        "table-wrap panel",
        density === "compact" && "density-compact",
        density === "comfy" && "density-comfy",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--hairline)] px-3 py-1.5">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.08em] text-[color:var(--faint)]">
          {rows.length} rows
        </div>
        <div className="flex items-center gap-2">
          {toolbar}
          <div className="segmented" role="group" aria-label="Row density">
            {(
              [
                ["compact", "Compact"],
                ["default", "Default"],
                ["comfy", "Comfy"],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={density === v ? "active" : ""}
                onClick={() => setDensity(v)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <table className="catalog">
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = c.sortable != null && onSort != null;
              const active = sortable && sortKey === c.sortable;
              const ariaSort = !sortable
                ? undefined
                : active
                  ? sortDir === "asc"
                    ? "ascending"
                    : "descending"
                  : "none";

              if (!sortable) {
                return (
                  <th
                    key={c.id}
                    className={c.headerClassName}
                    style={c.width != null ? { width: c.width } : undefined}
                  >
                    {c.header}
                  </th>
                );
              }

              return (
                <th
                  key={c.id}
                  className={cn(
                    c.headerClassName,
                    "th-sortable",
                    active && "th-sorted",
                  )}
                  style={c.width != null ? { width: c.width } : undefined}
                  aria-sort={ariaSort}
                >
                  <button
                    type="button"
                    className="th-sort-btn"
                    onClick={() => onSort(c.sortable!)}
                  >
                    <span>{c.header}</span>
                    <span className="th-sort-icon" aria-hidden>
                      {active && sortDir === "asc" ? (
                        <ChevronUp size={12} strokeWidth={2.5} />
                      ) : active && sortDir === "desc" ? (
                        <ChevronDown size={12} strokeWidth={2.5} />
                      ) : (
                        <ChevronUp size={12} strokeWidth={2} className="th-sort-hint" />
                      )}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {slice.map((row) => (
            <tr key={row.id} tabIndex={0}>
              {columns.map((c) => (
                <td key={c.id} className={c.className}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pageCount > 1 && (
        <div className="table-pager">
          <button
            type="button"
            className="btn ghost sm"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <span className="muted">
            Page {safePage + 1} / {pageCount} · {rows.length} rows
          </span>
          <button
            type="button"
            className="btn ghost sm"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
